import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Alert, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faUser, faUserPlus, faCloud, faCheckCircle, faShield, faFileAlt, faSearch } from '@fortawesome/free-solid-svg-icons';
import './Register.css';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  // Initial form values
  const initialValues = {
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await register(values.name, values.email, values.password);
      
      if (response.status === 201) {
        // Registration successful
        setRegistrationSuccess(true);
        setPendingEmail(values.email);
        
        // Redirect to verification pending page after a short delay
        setTimeout(() => {
          navigate('/verification-pending', { state: { email: values.email } });
        }, 2000);
      } else {
        // Registration failed
        setError(response.message || 'Registration failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-page-wrapper">
        {/* Left Panel - Branding & Features */}
        <div className="register-brand-panel">
          <div className="brand-content">
            <div className="brand-logo">
              <FontAwesomeIcon icon={faCloud} size="3x" />
            </div>
            <h1 className="brand-title">Vcurd Document Review</h1>
            <p className="brand-tagline">Intelligent Document Management System</p>
            
            <div className="mt-5 pt-3">
              <h2 className="features-title">Why Choose Us?</h2>
              <p className="features-subtitle">Streamline your document compliance workflow</p>
              
              <div className="feature-item">
                <div className="feature-icon-wrapper">
                  <FontAwesomeIcon icon={faShield} className="feature-icon" />
                </div>
                <div className="feature-text">
                  <h5>Enhanced Security</h5>
                  <p>Advanced encryption for all your sensitive documents</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon-wrapper">
                  <FontAwesomeIcon icon={faFileAlt} className="feature-icon" />
                </div>
                <div className="feature-text">
                  <h5>Compliance Frameworks</h5>
                  <p>Support for 30+ regulatory compliance frameworks</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="feature-icon" />
                </div>
                <div className="feature-text">
                  <h5>AI-Powered Analysis</h5>
                  <p>Intelligent document review and compliance checking</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Panel - Registration Form */}
        <div className="register-form-panel">
          <div className="register-form-container">
            <div className="d-none d-md-block">
              <h2 className="register-title">Create Your Account</h2>
              <p className="register-subtitle">Join thousands of professionals managing document compliance</p>
            </div>
            
            <div className="d-block d-md-none text-center mb-4">
              <FontAwesomeIcon icon={faCloud} size="2x" className="text-primary mb-2" />
              <h2 className="brand-title-mobile">Vcurd Document Review</h2>
            </div>
            
            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}
            
            {registrationSuccess ? (
              <div className="success-message text-center py-4">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success mb-3" size="4x" />
                <h4>Registration Successful!</h4>
                <p className="mb-3">We've sent a verification link to <strong>{pendingEmail}</strong></p>
                <p>Please check your email inbox and click the verification link to activate your account.</p>
                <div className="mt-4">
                  <p className="text-muted">Redirecting to verification page...</p>
                </div>
              </div>
            ) : (
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <div className="input-group-custom mb-4">
                      <div className="icon-wrapper">
                        <FontAwesomeIcon icon={faUser} className="input-icon" />
                      </div>
                      <Field
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder="Full Name"
                      />
                      <ErrorMessage name="name" component="div" className="error-message" />
                    </div>

                    <div className="input-group-custom mb-4">
                      <div className="icon-wrapper">
                        <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                      </div>
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Email Address"
                      />
                      <ErrorMessage name="email" component="div" className="error-message" />
                    </div>

                    <div className="input-group-custom mb-4">
                      <div className="icon-wrapper">
                        <FontAwesomeIcon icon={faLock} className="input-icon" />
                      </div>
                      <Field
                        type="password"
                        id="password"
                        name="password"
                        className="form-control"
                        placeholder="Password (min. 8 characters)"
                      />
                      <ErrorMessage name="password" component="div" className="error-message" />
                    </div>

                    <div className="input-group-custom mb-4">
                      <div className="icon-wrapper">
                        <FontAwesomeIcon icon={faLock} className="input-icon" />
                      </div>
                      <Field
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        className="form-control"
                        placeholder="Confirm Password"
                      />
                      <ErrorMessage name="confirmPassword" component="div" className="error-message" />
                    </div>

                    <div className="d-grid gap-2 mt-4 mb-4">
                      <button 
                        type="submit" 
                        className="register-btn btn btn-primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating Account...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                            Create Account
                          </>
                        )}
                      </button>
                    </div>
                    
                    <div className="register-footer text-center">
                      <p className="mb-0">
                        Already have an account? <Link to="/login">Sign In</Link>
                      </p>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
